import { useCallback, useEffect, useRef, useState } from "react";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";
import type { Session, SessionRunResponse } from "@/lib/session";
import {
  isSubAgentSessionData,
  isTerminalSubAgentState,
  subAgentStateFromRunStatus,
  type SubAgentThreadReference,
  type SubAgentThreadState,
} from "@/lib/sub-agent-thread";

export const SUB_AGENT_ACTIVE_POLL_INTERVAL_MS = 1_000;
export const SUB_AGENT_STALE_WINDOW_MS = 30_000;

export type SubAgentSessionAvailability =
  | "available"
  | "access_denied"
  | "unavailable";

export interface UseSubAgentSessionStateOptions {
  enabled?: boolean;
  /** Known transcript size; omit when the hook should sample the message feed. */
  messageCount?: number;
  /** Lifecycle inferred by a caller that already owns the transcript feed. */
  messageState?: SubAgentThreadState | null;
  pollIntervalMs?: number;
  staleWindowMs?: number;
}

/** Minimum durable identity needed to follow a child session. */
export type SubAgentSessionTarget = Pick<
  SubAgentThreadReference,
  "childSessionId" | "parentSessionId" | "state" | "viewAvailable"
>;

export interface UseSubAgentSessionStateReturn {
  session: Session | null;
  runs: SessionRunResponse[];
  state: SubAgentThreadState;
  availability: SubAgentSessionAvailability;
  loading: boolean;
  staleEmpty: boolean;
  retry: () => void;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function runsFromResponse(value: unknown): SessionRunResponse[] {
  if (!isRecord(value) || !Array.isArray(value.runs)) return [];
  return value.runs.filter((run): run is SessionRunResponse => (
    isRecord(run)
    && typeof run.run_id === "string"
    && typeof run.status === "string"
  ));
}

export interface SubAgentMessageObservation {
  count: number;
  state: SubAgentThreadState | null;
}

/** Infer lifecycle from the newest persisted transcript row, when present. */
export function subAgentMessageStateFromRows(
  rows: readonly unknown[],
): SubAgentThreadState | null {
  if (rows.length === 0) return null;
  const newest = rows.at(-1);
  if (!isRecord(newest)) return "working";
  const metadata = isRecord(newest.metadata) ? newest.metadata : {};
  const statusValue = newest.status ?? metadata.status;
  const status = typeof statusValue === "string" ? statusValue.toLowerCase() : "";
  const phase = typeof metadata.phase === "string" ? metadata.phase : "";
  const resolved = metadata.resolved === true || metadata.hitlResolved === true;

  if (phase === "hitl_paused" && !resolved) return "paused";
  if (["error", "failed"].includes(status) || metadata.error === true) return "failed";
  if (["cancelled", "canceled"].includes(status)) return "cancelled";
  if (["timed_out", "timeout"].includes(status)) return "timed_out";
  if (
    newest.role === "assistant"
    && ["complete", "completed", "success", "succeeded"].includes(status)
  ) {
    return "completed";
  }
  return "working";
}

function messageObservationFromResponse(value: unknown): SubAgentMessageObservation {
  if (!isRecord(value) || !Array.isArray(value.messages)) {
    return { count: 0, state: null };
  }
  const count = typeof value.total === "number" && value.total >= 0
    ? value.total
    : value.messages.length;
  return {
    count,
    state: subAgentMessageStateFromRows(value.messages)
      ?? (count > 0 ? "working" : null),
  };
}

function stateAfterSpawnHandle(state: SubAgentThreadState): SubAgentThreadState {
  return state === "queued" || state === "running" ? "working" : state;
}

function resolveSnapshotState(
  referenceState: SubAgentThreadState,
  loadedRuns: readonly SessionRunResponse[],
  observedMessageState: SubAgentThreadState | null,
): SubAgentThreadState {
  const runState = subAgentStateFromRunStatus(loadedRuns[0]?.status);
  if (isTerminalSubAgentState(referenceState)) return referenceState;
  if (runState && isTerminalSubAgentState(runState)) return runState;
  if (observedMessageState && isTerminalSubAgentState(observedMessageState)) {
    return observedMessageState;
  }
  if (observedMessageState === "paused" || runState === "paused") return "paused";
  return observedMessageState
    ?? runState
    ?? stateAfterSpawnHandle(referenceState);
}

/**
 * Follow child detail and run state only while its lifecycle is nonterminal.
 * Authorization and provenance failures are fail-closed and never retried in
 * a loop; the caller can offer the scoped `retry` action instead.
 */
export function useSubAgentSessionState(
  reference: SubAgentSessionTarget,
  fetchWithAuth: (url: string, init: RequestInit) => Promise<Response>,
  {
    enabled = true,
    messageCount,
    messageState = null,
    pollIntervalMs = SUB_AGENT_ACTIVE_POLL_INTERVAL_MS,
    staleWindowMs = SUB_AGENT_STALE_WINDOW_MS,
  }: UseSubAgentSessionStateOptions = {},
): UseSubAgentSessionStateReturn {
  const [session, setSession] = useState<Session | null>(null);
  const [runs, setRuns] = useState<SessionRunResponse[]>([]);
  const [state, setState] = useState<SubAgentThreadState>(
    stateAfterSpawnHandle(reference.state),
  );
  const [availability, setAvailability] =
    useState<SubAgentSessionAvailability>("available");
  const [loading, setLoading] = useState(enabled && reference.viewAvailable);
  const [staleEmpty, setStaleEmpty] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const emptyBaselineRef = useRef<{ lastActiveAt: string; observedAt: number } | null>(null);
  const staleEmptyRef = useRef(false);

  const retry = useCallback(() => {
    setAvailability("available");
    setLoading(true);
    setStaleEmpty(false);
    staleEmptyRef.current = false;
    emptyBaselineRef.current = null;
    setRetryNonce((nonce) => nonce + 1);
  }, []);

  useRefreshOnReconnect(retry, enabled && reference.viewAvailable);

  useEffect(() => {
    setSession(null);
    setRuns([]);
    setState(stateAfterSpawnHandle(reference.state));
    setAvailability("available");
    setLoading(enabled && reference.viewAvailable);
    setStaleEmpty(false);
    staleEmptyRef.current = false;
    emptyBaselineRef.current = null;
  }, [enabled, reference.childSessionId, reference.state, reference.viewAvailable]);

  useEffect(() => {
    if (!enabled || !reference.viewAvailable) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let staleTimer: ReturnType<typeof setTimeout> | null = null;
    let controller: AbortController | null = null;

    const clearTimers = () => {
      if (pollTimer) clearTimeout(pollTimer);
      if (staleTimer) clearTimeout(staleTimer);
      pollTimer = null;
      staleTimer = null;
    };

    const scheduleStaleCheck = (
      loaded: Session,
      loadedRuns: SessionRunResponse[],
      latestState: SubAgentThreadState,
      observedMessageCount: number,
    ) => {
      if (
        loadedRuns.length > 0
        || observedMessageCount > 0
        || isTerminalSubAgentState(latestState)
      ) {
        if (staleTimer) clearTimeout(staleTimer);
        staleTimer = null;
        emptyBaselineRef.current = null;
        staleEmptyRef.current = false;
        setStaleEmpty(false);
        return;
      }
      const now = Date.now();
      const baseline = emptyBaselineRef.current;
      if (!baseline || baseline.lastActiveAt !== loaded.last_active_at) {
        emptyBaselineRef.current = {
          lastActiveAt: loaded.last_active_at,
          observedAt: now,
        };
      }
      const observedAt = emptyBaselineRef.current?.observedAt ?? now;
      const remaining = staleWindowMs - (now - observedAt);
      if (remaining <= 0) {
        staleEmptyRef.current = true;
        setStaleEmpty(true);
        return;
      }
      if (staleTimer) clearTimeout(staleTimer);
      staleTimer = setTimeout(() => {
        if (!cancelled && observedMessageCount === 0) {
          staleEmptyRef.current = true;
          setStaleEmpty(true);
        }
      }, remaining);
    };

    const fetchSnapshot = async () => {
      controller?.abort();
      controller = new AbortController();
      const signal = controller.signal;
      try {
        const encodedId = encodeURIComponent(reference.childSessionId);
        const [sessionResponse, runsResponse, messagesResponse] = await Promise.all([
          fetchWithAuth(`/api/sessions/${encodedId}`, { headers: {}, signal }),
          fetchWithAuth(`/api/sessions/${encodedId}/runs?limit=1`, { headers: {}, signal })
            .catch(() => null),
          messageCount === undefined
            ? fetchWithAuth(
                `/api/sessions/${encodedId}/messages?limit=1&complete_turns=true`,
                { headers: {}, signal },
              )
            : Promise.resolve(null),
        ]);
        if (cancelled || signal.aborted) return;

        if (
          [401, 403, 404].includes(sessionResponse.status)
          || (messagesResponse !== null
            && [401, 403, 404].includes(messagesResponse.status))
        ) {
          setAvailability("access_denied");
          setSession(null);
          setRuns([]);
          setLoading(false);
          return;
        }
        if (!sessionResponse.ok || (messagesResponse !== null && !messagesResponse.ok)) {
          throw new Error(
            `HTTP ${sessionResponse.status}/${messagesResponse?.status ?? "feed-owned"}`,
          );
        }

        const loadedSession = await sessionResponse.json() as Session;
        let loadedRuns: SessionRunResponse[] = [];
        if (runsResponse?.ok) {
          try {
            loadedRuns = runsFromResponse(await runsResponse.json());
          } catch {
            // Direct sub-agent runs may have no RunManager representation;
            // malformed/legacy run data is therefore optional evidence only.
          }
        }
        const sampledMessages = messagesResponse
          ? messageObservationFromResponse(await messagesResponse.json())
          : null;
        const observedMessageCount = messageCount ?? sampledMessages?.count ?? 0;
        const observedMessageState = messageState ?? sampledMessages?.state ?? null;
        if (cancelled || signal.aborted) return;
        if (
          loadedSession.id !== reference.childSessionId
          || !isSubAgentSessionData(loadedSession)
          || loadedSession.parent_session_id !== reference.parentSessionId
        ) {
          setAvailability("access_denied");
          setSession(null);
          setRuns([]);
          setLoading(false);
          console.warn("[sub-agent-thread] child provenance validation failed", {
            childSessionId: reference.childSessionId,
          });
          return;
        }

        const latestState = resolveSnapshotState(
          reference.state,
          loadedRuns,
          observedMessageState,
        );
        setSession(loadedSession);
        setRuns(loadedRuns);
        setState(latestState);
        setAvailability("available");
        setLoading(false);
        scheduleStaleCheck(
          loadedSession,
          loadedRuns,
          latestState,
          observedMessageCount,
        );

        if (!isTerminalSubAgentState(latestState) && !staleEmptyRef.current) {
          pollTimer = setTimeout(() => {
            if (!cancelled) void fetchSnapshot();
          }, pollIntervalMs);
        }
      } catch (error) {
        if (cancelled || signal.aborted) return;
        setAvailability("unavailable");
        setLoading(false);
        console.info("[sub-agent-thread] child snapshot unavailable", {
          childSessionId: reference.childSessionId,
          error: error instanceof Error ? error.name : "unknown",
        });
      }
    };

    void fetchSnapshot();
    return () => {
      cancelled = true;
      controller?.abort();
      clearTimers();
    };
  }, [
    enabled,
    fetchWithAuth,
    messageCount,
    messageState,
    pollIntervalMs,
    reference.childSessionId,
    reference.parentSessionId,
    reference.state,
    reference.viewAvailable,
    retryNonce,
    staleWindowMs,
  ]);

  return {
    session,
    runs,
    state,
    availability,
    loading,
    staleEmpty,
    retry,
  };
}
